// components/public/LeadCaptureForm.tsx
// The interactive "Buyer's Playbook" email signup form (state, effects, fetch,
// GA4 funnel events). Split out of LeadCapture.tsx so pages that render only
// the static "picks" box (reviews, guides, verdicts) no longer ship this code.
// Copy and markup are unchanged; see LeadCapture.tsx for the copy history.
//
// This is a client component for form interactivity.

"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { ArrowRight, Check, BookOpen } from "lucide-react";
import type { LeadSource } from "@/lib/lead-sources";
import type { LeadCaptureProps } from "@/components/public/LeadCapture";
import PlaybookThankYou from "@/components/public/PlaybookThankYou";
import { hasAcceptedCookieConsent } from "@/lib/scroll-depth";
import {
  newPlaybookFunnelEvents,
  type PlaybookFunnelEvent,
  type PlaybookFunnelPhase,
} from "@/lib/playbook-funnel";
import {
  getLeadReturnFlag,
  getLeadReturnFlagServer,
  subscribeLeadReturnFlag,
} from "@/components/public/lead-return-flag";

type GtagWindow = typeof window & { gtag?: unknown };

// GA4 Playbook form funnel (Priority 293): playbook_form_view / _start /
// _submit, each at most once per mount (`sent` is the mount's own set).
// Sends only when the visitor has ALREADY accepted cookies (ff_consent=
// accepted) and window.gtag is a function; otherwise nothing is sent and
// nothing is marked sent, so a later interaction can still send it. The only
// param is the `source` prop when there is one — never the email address.
function sendPlaybookFunnelEvents(
  phase: PlaybookFunnelPhase,
  sent: Set<PlaybookFunnelEvent>,
  source: LeadSource | undefined,
) {
  const gtag = (window as GtagWindow).gtag;
  if (typeof gtag !== "function") return;
  const canSend = hasAcceptedCookieConsent(document.cookie);
  for (const name of newPlaybookFunnelEvents(phase, sent, canSend)) {
    sent.add(name);
    gtag("event", name, source ? { source } : {});
  }
}

export default function LeadCaptureForm({
  source,
}: Omit<LeadCaptureProps, "variant"> = {}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong. Please try again.",
  );

  // Native (no-JS) POSTs 303 back with ?subscribed=1 / ?leadError=1.
  // Hash forms (#subscribed=1) are the pre-fix spelling — still honoured so
  // a visitor who lands on a leftover URL still sees the confirmation.
  // Read via useSyncExternalStore, not useEffect+setState — the latter
  // fails react-hooks/set-state-in-effect.
  const returnFlag = useSyncExternalStore(
    subscribeLeadReturnFlag,
    getLeadReturnFlag,
    getLeadReturnFlagServer,
  );
  const visibleStatus = status === "idle" && returnFlag ? returnFlag : status;

  const sentFunnelEvents = useRef(new Set<PlaybookFunnelEvent>());
  const emailInputRef = useRef<HTMLInputElement>(null);

  // What the page loaded as. Read from the URL here rather than from
  // `returnFlag`: during hydration that is still the server snapshot (null),
  // which would count a view on a page that loaded already subscribed.
  useEffect(() => {
    const loadedAs = getLeadReturnFlag();
    sendPlaybookFunnelEvents(
      loadedAs === "success"
        ? "subscribed_on_load"
        : loadedAs === "error"
          ? "error"
          : "view",
      sentFunnelEvents.current,
      source,
    );
  }, [source]);

  const handleEmailStart = () => {
    sendPlaybookFunnelEvents("start", sentFunnelEvents.current, source);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus("loading");
    setErrorMessage("Something went wrong. Please try again.");

    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        // A 303 (the no-JS path) must not be followed and counted as
        // success — fetch follows redirects by default, and the followed
        // GET is 200 HTML even when the write failed.
        redirect: "error",
        body: JSON.stringify({ email, source }),
      });

      const contentType = res.headers.get("content-type") ?? "";
      if (!contentType.includes("application/json")) {
        throw new Error("Failed to subscribe");
      }
      const body = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !body.ok) {
        throw new Error(
          typeof body.error === "string" && body.error
            ? body.error
            : "Failed to subscribe",
        );
      }
      setStatus("success");
      setEmail("");
      sendPlaybookFunnelEvents("success", sentFunnelEvents.current, source);
    } catch (err) {
      setErrorMessage(
        err instanceof Error &&
          err.message &&
          err.message !== "Failed to subscribe" &&
          !/failed to fetch|networkerror|redirect/i.test(err.message)
          ? err.message
          : "Something went wrong. Please try again.",
      );
      setStatus("error");
      // Move focus to the field so keyboard and screen reader users land on
      // the described, invalid input after a failed submit.
      emailInputRef.current?.focus();
    }
  };

  if (visibleStatus === "success") {
    return (
      <section
        aria-label="Buyer's Playbook signup received"
        className="relative overflow-hidden rounded-2xl border border-forge-trust/20 bg-content-surface p-8 sm:p-12 text-center"
      >
        <div className="relative">
          <PlaybookThankYou />
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Email updates signup"
      className="relative overflow-hidden rounded-2xl border border-content-border bg-content-surface"
    >
      <div className="relative p-8 sm:p-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          {/* ── Value Proposition (Left) ────────────────────────── */}
          <div className="lg:max-w-lg">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={18} className="text-forge-trust" />
              <span className="text-base font-semibold uppercase tracking-widest text-forge-trust">
                Before you spend another dollar
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-content-text mb-3 tracking-tight">
              Get the Buyer&apos;s Playbook when it&apos;s ready
            </h2>
            <p className="text-content-muted leading-relaxed">
              A short checklist for any money tool you&apos;re about to pay for: what to read on the pricing page, in the terms and in the refund policy. Leave your email and we&apos;ll send it when it&apos;s ready.
            </p>

            {/* ── Trust Signals ───────────────────────────────────── */}
            {/* A <ul> so each note is its own item: as bare <span> siblings
                they were extracted as one run, "No spam, everWe'll only
                email you about the PlaybookWritten by our editorial team".
                data-nosnippet keeps this opt-in subtext out of search
                snippets; readers still see it. */}
            <ul
              data-nosnippet
              className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-base text-content-muted"
            >
              <li className="flex items-center gap-1.5">
                <Check size={13} className="text-forge-trust" aria-hidden="true" />
                One email when it&apos;s ready
              </li>
              {" "}
              <li className="flex items-center gap-1.5">
                <span className="h-1 w-1 rounded-full bg-content-muted/40" aria-hidden="true" />
                No spam, ever
              </li>
              {" "}
              {/* "Join 10,000+ smart buyers" was removed 2026-05-01 (#462)
                  — the count was unverifiable and read as inflated social
                  proof. Replaced with a falsifiable editorial signal: any
                  visitor can check the editorial team page at /about. */}
              <li className="flex items-center gap-1.5">
                <span className="h-1 w-1 rounded-full bg-content-muted/40" aria-hidden="true" />
                Written by our editorial team
              </li>
            </ul>
          </div>

          {/* ── Email Form (Right) ─────────────────────────────── */}
          <form
            onSubmit={handleSubmit}
            method="post"
            action="/api/leads"
            className="flex flex-col gap-3 lg:min-w-[380px]"
          >
            {visibleStatus === "error" && (
              <p id="lead-email-error" className="text-base text-forge-urgency" role="alert">
                {errorMessage}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
            <label htmlFor="lead-email" className="sr-only">
              Email address
            </label>
            {/* Mirrors the JSON body for the native no-JS submission path (#558).
                Without it, attribution would be present exactly when the page
                works and absent exactly when it does not — the reverse of what
                is useful. */}
            <input type="hidden" name="source" value={source ?? "unknown"} />
            <input
              ref={emailInputRef}
              id="lead-email"
              name="email"
              type="email"
              required
              aria-invalid={visibleStatus === "error" ? "true" : undefined}
              aria-describedby={visibleStatus === "error" ? "lead-email-error" : undefined}
              placeholder="your@email.com"
              value={email}
              onFocus={handleEmailStart}
              onChange={(e) => {
                setEmail(e.target.value);
                handleEmailStart();
              }}
              className="flex-1 rounded-lg border border-content-border bg-content-bg px-4 py-3.5 text-base text-content-text placeholder:text-content-muted outline-none transition-colors focus:border-forge-trust/40 focus:ring-1 focus:ring-forge-trust/20"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className=" flex items-center justify-center gap-2 rounded-lg bg-forge-trust px-6 py-3.5 text-base font-semibold text-content-bg transition-all duration-300 hover:bg-forge-trust-hover disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {status === "loading" ? (
                "Sending..."
              ) : (
                <>
                  Notify Me When It&apos;s Ready
                  <ArrowRight size={16} />
                </>
              )}
            </button>
            </div>
            {/* Inline Privacy notice — adjacent to the email field, satisfies
                GDPR/PECR/CASL expectations and lifts opt-in rates by surfacing
                the privacy promise where the friction is highest. See #392. */}
            <p data-nosnippet className="text-base leading-snug text-content-muted">
              By submitting, you agree to our{" "}
              <Link
                href="/privacy"
                className="underline decoration-content-muted/40 underline-offset-2 hover:text-content-text hover:decoration-content-text/60 transition-colors"
              >
                Privacy Policy
              </Link>
              . We&apos;ll never share your email.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
